/**
 * Полная диагностика системы авторизации
 *
 * Проверяет все компоненты и выводит детальный отчет
 *
 * Использование в консоли: window.$authCheck()
 */

import { consola } from 'consola'

const logger = consola.withTag('auth-check')

export const performAuthSystemCheck = async () => {
  logger.log('🔍 [AUTH SYSTEM CHECK] Диагностика системы авторизации')

  try {
    const { useSessionManager } = await import('~/entities/auth')
    const { useAuthStore } = await import('~/features/auth/model')
    const { useLogoutEventTracker } = await import('@features/auth')
    const { useLogoutHandler } = await import('~/features/auth/model')

    const sessionManager = useSessionManager()
    const authStore = useAuthStore()
    const tracker = useLogoutEventTracker()
    const logoutHandler = useLogoutHandler()

    // 1. Статус токенов
    logger.log('📊 1. Статус токенов')
    const remainingTime = sessionManager.getTokenRemainingTime()
    logger.log('✓ Access Token:', sessionManager.authAccessToken.value ? '✅ Есть' : '❌ Нет')
    logger.log('✓ Session ID:', sessionManager.authSessionId.value ? '✅ Есть' : '❌ Нет')
    logger.log('✓ Истек:', sessionManager.isAccessTokenExpired() ? '❌ Да' : '✅ Нет')
    logger.log('✓ Нужно обновить:', sessionManager.shouldRefreshToken() ? '⚠️ Да' : '✅ Нет')
    logger.log('✓ Осталось времени:', remainingTime === Infinity ? 'Не установлен' : `${Math.floor(remainingTime)} сек (${(remainingTime / 60).toFixed(2)} мин)`)

    // 2. Статус авторизации
    logger.log('🔐 2. Статус авторизации')
    logger.log('✓ Залогинен:', authStore.isLoggedIn ? '✅ Да' : '❌ Нет')
    logger.log('✓ Активная сессия:', sessionManager.hasActiveSession ? '✅ Да' : '❌ Нет')

    // 3. Счетчики ошибок
    logger.log('📈 3. Счетчики и флаги')
    logger.log('✓ Ошибки обновления токена:', logoutHandler.getRefreshTokenFailureCount(), '/ 2')
    logger.log('✓ Обновление в процессе:', logoutHandler.isTokenRefreshing() ? '🔄 Да' : '⏸️ Нет')
    logger.log('✓ Токен помечен недействительным:', logoutHandler.isRefreshTokenInvalid() ? '❌ Да' : '✅ Нет')

    // 4. История разлогиниваний
    logger.log('📜 4. История разлогиниваний')
    const history = tracker.getLogoutHistory()
    if (history.length > 0) {
      logger.log(`Найдено ${history.length} событий:`)
      for (const [index, event] of history.slice(0, 5).entries()) {
        logger.log(`  ${index + 1}. ${event.timestamp}`)
        logger.log(`     Причина: ${event.reason}`)
        logger.log(`     Источник: ${event.source}`)
      }
      if (history.length > 5) {
        logger.log(`  ... и еще ${history.length - 5} событий`)
      }
    } else {
      logger.log('✓ История пуста (хороший знак!)')
    }

    // 5. Cookies и Storage
    logger.log('🍪 5. Cookies и Storage')
    const cookies = document.cookie.split(';').map(c => c.trim())
    const authCookies = cookies.filter(c => c.includes('auth'))
    logger.log('✓ Auth Cookies:', authCookies.length > 0 ? authCookies : 'Нет')
    logger.log('✓ SessionStorage authSessionId:', sessionStorage.getItem('authSessionId') ? '✅ Есть' : '❌ Нет')
    logger.log('✓ LocalStorage refresh_token_invalid:', localStorage.getItem('refresh_token_invalid') ? '❌ Помечен' : '✅ OK')

    // 6. Константы времени
    logger.log('⏰ 6. Константы времени токенов')
    const { ACCESS_TOKEN_MAX_AGE_SECONDS } = await import('~/shared/config/auth')
    logger.log('✓ ACCESS_TOKEN_MAX_AGE:', `${ACCESS_TOKEN_MAX_AGE_SECONDS} сек (${ACCESS_TOKEN_MAX_AGE_SECONDS / 60} мин)`)
    logger.log('⚠️ ВНИМАНИЕ: Если токен истекает через 5 минут - это нормально по константе!')

    // 7. Рекомендации
    logger.log('💡 7. Рекомендации')
    if (!authStore.isLoggedIn) {
      logger.warn('⚠️ Пользователь не авторизован')
    } else if (sessionManager.isAccessTokenExpired()) {
      logger.error('🔴 Токен истек! Должно произойти автообновление или разлогинивание')
    } else if (sessionManager.shouldRefreshToken()) {
      logger.warn('⚠️ Токен скоро истечет, должен обновиться автоматически')
    } else if (remainingTime < 600) { // < 10 минут
      logger.warn(`⚠️ Осталось меньше 10 минут до истечения (${Math.floor(remainingTime / 60)} мин)`)
    } else {
      logger.log('✅ Все в порядке!')
    }


    // Возвращаем объект для программного доступа
    return {
      hasAccessToken: !!sessionManager.authAccessToken.value,
      hasSessionId: !!sessionManager.authSessionId.value,
      isLoggedIn: authStore.isLoggedIn,
      isExpired: sessionManager.isAccessTokenExpired(),
      shouldRefresh: sessionManager.shouldRefreshToken(),
      remainingSeconds: remainingTime,
      failureCount: logoutHandler.getRefreshTokenFailureCount(),
      isRefreshing: logoutHandler.isTokenRefreshing(),
      logoutHistoryCount: history.length,
      lastLogout: history[0] || null,
    }

  } catch (error) {
    logger.error('❌ Ошибка при диагностике:', error)
    return null
  }
}

// Экспортируем в window для быстрого доступа
if (process.client) {
  ;(window as any).$authCheck = performAuthSystemCheck
  logger.log('💡 Для полной диагностики системы авторизации выполните: window.$authCheck()')
}

